import { unref } from 'vue'

import { useAuthStore } from '@common/auth-layer/store/auth.store'
import { hydrateCache } from '@common/core-layer/utils/cache-hydration.utils'
import { useGlobal } from '@common/core-layer/composables/useGlobal'

import { communityUserQueryKeys } from '@communities/community-user-layer/utils/query-keys.utils'
import { GroupMemberRole, type GroupListItemWithEmbeddedMembership } from '@communities/groups-layer/types/types'
import { getNormalizedGroupTabsConfiguration } from '@communities/groups-layer/utils/group-tabs-configuration.utils'
import { groupsQueryKeys } from '@communities/groups-layer/utils/query-keys.utils'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { useNuxtApp } from 'nuxt/app'
import {
  buildMemberBadgeHash,
  buildMembersQueryWithoutBadge,
  readMemberBadgeTemplateIdFromQuery,
} from '@communities/members-layer/utils/member-badge-query.utils'
import type { SSRNuxtAppLike } from '@common/http-layer/service/base.service'
import type { GroupTabName } from '@communities/groups-layer/utils/constants'

import { LEGACY_GROUP_ROUTE_ALIASES } from '../constants/legacy-group-route-aliases'
import { CONFIGURABLE_TAB_BY_ROUTE_SEGMENT } from '../constants/group-route-segments'
import {
  getFirstSegmentAfterSlug,
  getGroupNotFoundPath,
  getGroupRouteRedirect,
  isContentUnavailablePath,
  isMembershipEmptyStateTabPath,
  isValidGroupPath,
  normalizeGroupRouteSlug,
} from '../guards/group-route-guard'
import { fetchGroupData, fetchUserMembershipData, type GroupData, type UserMembershipData } from '../services/middleware-data.service'
import type { InfiniteData, QueryClient } from '@tanstack/vue-query'
import type { Pinia } from 'pinia'

interface MiddlewareRoute {
  path: string
  params: Record<string, string | string[]>
  query?: Record<string, unknown>
  hash?: string
}

/** Default list only (no search). Skip search results. */
function getGroupFromListCache(queryClient: QueryClient, groupSlug: string): GroupListItemWithEmbeddedMembership | null {
  if (process.server) return null
  const data = queryClient.getQueryData(groupsQueryKeys.groups()) as InfiniteData<GroupListItemWithEmbeddedMembership[]> | undefined
  if (!data?.pages) return null
  const flat = data.pages.flat()
  return flat.find((g: GroupListItemWithEmbeddedMembership) => g.slug === groupSlug) ?? null
}

function groupFromListItem(listItem: GroupListItemWithEmbeddedMembership): GroupData {
  return {
    groupDetail: {
      _id: listItem._id,
      locationId: listItem.locationId,
      slug: listItem.slug,
      settings: {
        groupType: listItem.settings?.groupType,
        tabsConfiguration: listItem.settings?.tabsConfiguration,
      },
    },
  }
}

function membershipFromListItem(listItem: GroupListItemWithEmbeddedMembership): UserMembershipData | null {
  const detail = listItem.userGroupDetails ?? listItem.userGroupDetail ?? listItem['user-group-detail']
  if (!detail?.status) return null
  return {
    _id: detail._id ?? '',
    contactId: detail.contactId ?? '',
    groupId: detail.groupId ?? listItem._id,
    locationId: detail.locationId ?? listItem.locationId,
    status: detail.status,
    role: detail.role ?? '',
  }
}

/** Resolves group: cache (groupBySlug) → list cache → API. If cacheOnly, only groupBySlug cache. */
async function getGroupDetails(
  queryClient: QueryClient,
  groupSlug: string,
  options?: { cacheOnly?: boolean; ssrNuxtApp?: SSRNuxtAppLike }
): Promise<{ group: GroupData | null; listItem?: GroupListItemWithEmbeddedMembership | null }> {
  const cached = queryClient.getQueryData(groupsQueryKeys.groupBySlug(groupSlug)) as GroupData | undefined
  if (cached?.groupDetail) {
    return { group: cached }
  }
  if (options?.cacheOnly) {
    return { group: null }
  }

  const listItem = getGroupFromListCache(queryClient, groupSlug)
  if (listItem?.settings?.groupType) {
    return { group: groupFromListItem(listItem), listItem }
  }

  const group = (await hydrateCache({
    queryClient,
    queryKey: groupsQueryKeys.groupBySlug(groupSlug),
    fetchFn: () => fetchGroupData(groupSlug, options?.ssrNuxtApp),
  })) as GroupData | null
  return { group }
}

/** Resolves membership: cache (userGroupDetails) → list item → API. */
async function getMembershipDetails(
  queryClient: QueryClient,
  group: GroupData,
  listItem?: GroupListItemWithEmbeddedMembership | null,
  ssrNuxtApp?: SSRNuxtAppLike
): Promise<UserMembershipData | null> {
  const { _id: groupId, locationId } = group.groupDetail!
  // Defense-in-depth: never fetch membership without both ids. Callers are expected
  // to have validated the group already, but a missing id here means we cannot resolve
  // membership — return null rather than calling the API with `undefined`.
  if (!groupId || !locationId) {
    return null
  }
  const cached = queryClient.getQueryData(communityUserQueryKeys.userGroupDetails(groupId, locationId)) as UserMembershipData | undefined
  if (cached) {
    return cached
  }
  if (listItem) {
    const fromList = membershipFromListItem(listItem)
    if (fromList) {
      return fromList
    }
  }
  return (await hydrateCache({
    queryClient,
    queryKey: communityUserQueryKeys.userGroupDetails(groupId, locationId),
    fetchFn: () => fetchUserMembershipData(groupId, locationId, ssrNuxtApp),
  })) as UserMembershipData | null
}

export interface GroupAccessMiddlewareNuxtApp {
  $queryClient: QueryClient
  $pinia: Pinia
  /**
   * Nuxt `runWithContext`.
   * On the SERVER, Nuxt wraps the call in `executeAsync` and returns a Promise.
   * On the CLIENT, it returns the function's return value synchronously.
   * Tests may pass a plain `(fn) => fn()` stub (synchronous).
   */
  runWithContext: <T>(fn: () => T) => T | Promise<Awaited<T>>
  /** SSR-only: request-scoped context; used to plumb auth state to nitro data fetches. */
  ssrContext?: { event?: unknown } | null
  /** Shared single-flight slot for 401 refresh dedup across concurrent SSR requests. */
  _refreshInFlight?: Promise<boolean>
}

export interface GroupAccessMiddlewareOptions {
  nuxtApp?: GroupAccessMiddlewareNuxtApp
}

const REDIRECT_OPTIONS = { replace: true, redirectCode: 302 as const }

const ROUTE_NAMES = {
  about: 'communities-groups-about',
  home: 'communities-groups-home',
  contentUnavailable: 'communities-groups-content-unavailable',
} as const

function isGroupTabHidden(group: GroupData, membership: UserMembershipData | null, tabName: GroupTabName): boolean {
  const isGroupAdmin = membership?.role === GroupMemberRole.ADMIN || membership?.role === GroupMemberRole.OWNER
  const normalizedTabs = getNormalizedGroupTabsConfiguration(group.groupDetail?.settings?.tabsConfiguration, { isGroupAdmin })

  return normalizedTabs.some(tab => tab.name === tabName && !tab.isVisible)
}

const getStatusCodeFromError = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined
  const errorRecord = error as Record<string, unknown>
  const directStatus = errorRecord.statusCode
  if (typeof directStatus === 'number') return directStatus
  const status = errorRecord.status
  if (typeof status === 'number') return status

  const response = errorRecord.response
  if (response && typeof response === 'object') {
    const r = response as Record<string, unknown>
    const responseStatus = r.status
    if (typeof responseStatus === 'number') return responseStatus
    const responseStatusCode = r.statusCode
    if (typeof responseStatusCode === 'number') return responseStatusCode
  }

  const data = errorRecord.data
  if (data && typeof data === 'object') {
    const dataStatus = (data as Record<string, unknown>).statusCode
    if (typeof dataStatus === 'number') return dataStatus
  }

  const apiResponse = errorRecord.apiResponse
  if (!apiResponse || typeof apiResponse !== 'object') return undefined
  const nestedStatus = (apiResponse as Record<string, unknown>).statusCode
  return typeof nestedStatus === 'number' ? nestedStatus : undefined
}

export async function groupAccessMiddleware(
  to: { path: string; params: Record<string, string>; query?: Record<string, unknown>; hash?: string },
  from: { params: Record<string, string>; path: string },
  options?: GroupAccessMiddlewareOptions
) {
  // Capture nuxtApp + CPNavigateTo before any await so context survives async gaps.
  const nuxtApp = (options?.nuxtApp ?? useNuxtApp()) as GroupAccessMiddlewareNuxtApp
  // useCPNavigation → useGlobal → useRuntimeConfig must run inside nuxtApp.runWithContext
  // (SSR async context is not always active for nested composables otherwise).
  //
  // IMPORTANT: `runWithContext` returns a Promise on SSR (Nuxt wraps the call in
  // `executeAsync`) and a sync value on the client. `await` handles both cases —
  // `await value` resolves to `value` when `value` is not a Promise. Omitting
  // the await left `CPNavigateTo` as `undefined` on the server and caused
  // `CPNavigateTo is not a function` inside `doRedirectToRoute`.
  const { CPNavigateTo, isMobileOrTablet, isGokollab } = await nuxtApp.runWithContext(() => {
    const { CPNavigateTo: navigate } = useCPNavigation()
    const global = useGlobal()
    return {
      CPNavigateTo: navigate,
      isMobileOrTablet: global.isMobileOrTablet,
      isGokollab: unref(global.isGokollab),
    }
  })

  const doRedirectToRoute = (name: string, params: Record<string, string>, query?: Record<string, string>, hash?: string) =>
    nuxtApp.runWithContext(() => CPNavigateTo({ name, params, query, hash, options: REDIRECT_OPTIONS }))
  // Not-found redirect uses context-specific paths (e.g. '/' for GoKollab, '/communities/groups/not-found' for default)
  // that don't map 1:1 to a single named route, so we use navigateTo(path) with the precomputed path.
  const doRedirectToPath = (path: string) => nuxtApp.runWithContext(() => navigateTo(path, REDIRECT_OPTIONS))

  // CRITICAL: Skip app-level routes immediately to prevent infinite loops
  if (
    to.path === '/account' ||
    to.path.startsWith('/account/') ||
    to.path === '/dashboard' ||
    to.path === '/theme-playground' ||
    to.path.startsWith('/courses') ||
    to.path.startsWith('/communities/groups/not-found')
  ) {
    return
  }

  const groupSlug = normalizeGroupRouteSlug(to.params.groupSlug as string)
  if (!groupSlug) return

  const segmentAfterSlug = getFirstSegmentAfterSlug(to.path, groupSlug)
  const configurableTabName = segmentAfterSlug ? CONFIGURABLE_TAB_BY_ROUTE_SEGMENT[segmentAfterSlug] : undefined
  const memberStatusParam = to.query?.memberStatus
  const normalizedMemberStatus = Array.isArray(memberStatusParam)
    ? String(memberStatusParam[0] ?? '')
    : typeof memberStatusParam === 'string'
      ? memberStatusParam
      : ''

  const isMembersRoute = segmentAfterSlug === 'members' || segmentAfterSlug === 'members-list'

  // Legacy `?badge=<templateId>` deep-links canonicalize to `/members#badgeTemplateId=<templateId>`.
  // Badge hash and role/status hash are mutually exclusive — badge query wins over memberStatus.
  if (isMembersRoute) {
    const legacyBadgeTemplateId = readMemberBadgeTemplateIdFromQuery(to.query as Record<string, unknown>)
    const badgeHash = buildMemberBadgeHash(legacyBadgeTemplateId)
    if (badgeHash) {
      const nextQuery = buildMembersQueryWithoutBadge(to.query as Record<string, unknown>)
      return doRedirectToRoute(
        'communities-groups-members',
        { groupSlug },
        Object.keys(nextQuery).length > 0 ? nextQuery : undefined,
        badgeHash
      )
    }
  }

  // Notification deep-links may carry `memberStatus=Requested#active`.
  // `memberStatus=Requested` must win and canonicalize to `/members#requested`.
  if (isMembersRoute && normalizedMemberStatus.toLowerCase() === 'requested') {
    return doRedirectToRoute('communities-groups-members', { groupSlug }, undefined, 'requested')
  }

  // Block settings routes on mobile – redirect to group home.
  // Match the segment, not a substring: for a slug starting with "settings" the home path
  // contains "/settings" too, and the self-redirect spins SSR forever (prod vue-router has no
  // infinite-redirect abort).
  let isMobile = false
  nuxtApp.runWithContext(() => {
    isMobile = isMobileOrTablet()
  })
  if (isMobile && segmentAfterSlug === 'settings') {
    return doRedirectToRoute(ROUTE_NAMES.home, { groupSlug })
  }

  const queryClient = nuxtApp.$queryClient as QueryClient
  if (!queryClient) return

  const pinia = nuxtApp.$pinia as Pinia
  const authStore = useAuthStore(pinia)
  const isAuthenticated = authStore.isLoggedIn

  const fromGroupSlug = from.params.groupSlug ? normalizeGroupRouteSlug(from.params.groupSlug as string) : undefined
  const hasRealFromRoute = from.path != null && from.path !== to.path
  const isSameGroup = hasRealFromRoute && groupSlug === fromGroupSlug
  // Leaving the banned-member fallback page MUST re-validate. Otherwise a banned user
  // can navigate to any other tab via NuxtLink/URL and the layout's hidden chrome won't
  // help — the page itself would render. Forces getGroupRouteRedirect to run again.
  const isLeavingContentUnavailable = isSameGroup && isContentUnavailablePath(from.path, fromGroupSlug)

  // Only skip re-validation for same-group navigation onto About or empty-state **tab index**
  // routes (home). Configurable tabs (learning/events/…) still re-check so hidden-tab
  // redirects keep working; getGroupRouteRedirect then allows them via isSameGroupNavigation.
  const isAlwaysPublicGroupSegment = segmentAfterSlug === 'about' || segmentAfterSlug === 'not-found'
  const isMembershipEmptyStateSegment = isMembershipEmptyStateTabPath(to.path, groupSlug) && !configurableTabName
  const isEnteringContentUnavailable = isContentUnavailablePath(to.path, groupSlug)
  if (
    isSameGroup &&
    isValidGroupPath(to.path, groupSlug) &&
    !isLeavingContentUnavailable &&
    !isEnteringContentUnavailable &&
    (isAlwaysPublicGroupSegment || isMembershipEmptyStateSegment)
  ) {
    return
  }

  // On SSR, thread the captured nuxtApp into nitro-scoped data fetches so BaseService
  // can attach the auth token and run the 401 refresh chain when `useNuxtApp()` is
  // unreachable from inside the service instance.
  const ssrNuxtApp: SSRNuxtAppLike | undefined = process.server
    ? { $pinia: nuxtApp.$pinia, ssrContext: nuxtApp.ssrContext ?? null }
    : undefined

  let group: GroupData | null = null
  let listItem: GroupListItemWithEmbeddedMembership | null | undefined
  try {
    // cacheOnly only applies when isSameGroup bypasses — invalid-path same-group flows fall through and must fetch
    const result = await getGroupDetails(queryClient, groupSlug, { cacheOnly: false, ssrNuxtApp })
    group = result.group
    listItem = result.listItem
  } catch (error) {
    const statusCode = getStatusCodeFromError(error)
    if (statusCode === 404) {
      // Only true 404 means the group does not exist.
      group = null
      listItem = undefined
    } else {
      // For transient/auth/bootstrap failures (e.g. missing location context on cold load),
      // avoid forcing a not-found redirect and let page-level logic continue.
      return
    }
  }

  // A resolved group MUST carry both `_id` and `locationId`. A partial/malformed
  // groupDetail (truthy object missing ids) would otherwise flow into the membership
  // fetch and call `/communities/undefined/groups/undefined/user-group-details` → 500
  // (and, via the catch → contentUnavailable redirect, loop). Treat it as not-found.
  if (!group?.groupDetail?._id || !group.groupDetail.locationId) {
    return doRedirectToPath(getGroupNotFoundPath(isGokollab))
  }

  // Membership is required for BOTH private (to gate access) and public (to detect Banned status).
  // For unauthenticated users we skip the membership call entirely — no membership row can exist.
  let membership: UserMembershipData | null = null
  if (isAuthenticated) {
    try {
      membership = await getMembershipDetails(queryClient, group, listItem, ssrNuxtApp)
    } catch {
      // Transient/auth API failure: cannot determine ban status. Fail closed so a
      // BANNED user is never admitted when the membership service is degraded.
      return doRedirectToRoute(ROUTE_NAMES.contentUnavailable, { groupSlug })
    }
  }

  const redirect = getGroupRouteRedirect({
    path: to.path,
    groupSlug,
    group,
    membership,
    isAuthenticated,
    isSameGroupNavigation: isSameGroup,
  })

  if (configurableTabName && isGroupTabHidden(group, membership, configurableTabName) && (!redirect || redirect.redirect === 'home')) {
    return doRedirectToRoute(ROUTE_NAMES.home, { groupSlug })
  }

  if (!redirect) return

  switch (redirect.redirect) {
    case 'home': {
      const legacySegment = getFirstSegmentAfterSlug(to.path, groupSlug)
      const legacyTarget = legacySegment ? LEGACY_GROUP_ROUTE_ALIASES[legacySegment] : undefined
      if (legacyTarget) {
        return doRedirectToRoute(legacyTarget, { groupSlug })
      }
      return doRedirectToRoute(ROUTE_NAMES.home, { groupSlug })
    }
    case 'about': {
      const query: Record<string, string> = {}
      if (redirect.accessDenied && isValidGroupPath(to.path, groupSlug)) {
        query.reason = 'access_denied'
      }
      // Preserve invitationId across the About bounce so the group layout can
      // still auto-accept the invite once the user lands (logs in) there.
      const invitationId = to.query?.invitationId
      if (typeof invitationId === 'string' && invitationId) {
        query.invitationId = invitationId
      }
      return doRedirectToRoute(ROUTE_NAMES.about, { groupSlug }, Object.keys(query).length ? query : undefined)
    }
    case 'not_found':
      return doRedirectToPath(getGroupNotFoundPath(isGokollab))
    case 'content_unavailable':
      return doRedirectToRoute(ROUTE_NAMES.contentUnavailable, { groupSlug })
    default:
      return
  }
}

export default defineNuxtRouteMiddleware(
  (to: MiddlewareRoute, from: MiddlewareRoute) =>
    groupAccessMiddleware(
      { path: to.path, params: to.params as Record<string, string>, query: to.query, hash: to.hash },
      { params: from.params as Record<string, string>, path: from.path }
    ) as Promise<void>
)
